import MDBox from 'components/MDBox';
import Fade from '@mui/material/Fade';
import { darken } from '@mui/system';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import FileMetadatas from 'pages/fileViewer/components/FileMetadatas';
import { useFindBySpecificationQuery, useGetEntityRelationshipsQuery } from 'services/entityApi';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryData } from 'hook/useQueryData';
import RelatedDocumentsCarousel from 'pages/fileViewer/components/carousel/RelatedDocumentsCarousel';
import { Fab, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import PropTypes from 'prop-types';
import * as StringUtils from 'utils/stringUtils';
import { useGetConfigurationQuery } from 'services/applicationApi';
import { ToggleButton, ToggleButtonGroup } from '@mui/lab';
import Icon from '@mui/material/Icon';
import { useNotification } from 'hook/useNotification';
import { useSelector } from 'react-redux';
import FileViewer from './components/viewer/FileViewer';
import useConf from '../../hook/useConf';

/**
 * FileViewerContainer is a component that retrieves and presents file metadata and content.
 * The file to display is based on a hash parameter in the URL. If the hash isn't found, the component will display
 * data based on the file previously stored in the local storage. The file's metadata and content are fetched from
 * server.
 *
 * @component
 * @example
 * <FileViewerContainer />
 *
 * @returns {JSX.Element} The FileViewerContainer component which displays the file's metadata and its content.
 * If the content is still loading, a progress bar is shown instead.
 */
function FileViewerContainer({ onEntityUpdated }) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();
  const { getValue: conf } = useConf(useGetConfigurationQuery);

  const theme = useTheme();

  const primaryColor = theme?.palette.primary.main;
  const xlWidthUp = useMediaQuery(theme.breakpoints.up('xxl'));

  const { hash } = useParams();
  const [searchParams] = useSearchParams();
  const externalId = searchParams.get('externalId');

  const currentFile = useSelector((state) => state.fileViewer.file);
  const [file, setFile] = useState(currentFile);
  const [linkedFiles, setLinkedFiles] = useState(null);
  const [linkedFile, setLinkedFile] = useState(null);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [isCarouselEmpty, setIsCarouselEmpty] = useState(false);

  const fileViewerConf = useSelector((state) => state.fileViewer.conf) ?? conf('fileViewer');

  const handleCarouselStatus = useCallback((isEmpty) => {
    setIsCarouselEmpty(isEmpty);
  }, []);

  const externalCall = useMemo(() => Boolean(externalId || hash), [externalId, hash]);

  const { data: documentData, loading: documentLoading } = useQueryData(
    useFindBySpecificationQuery,
    {
      viewName: 'fileViewer',
      filter: externalId ? `externalId = ${externalId}` : `hash = ${hash}`,
      size: 1,
      baseType: 'DOCUMENT'
    },
    {
      skip: !externalCall
    }
  );

  useEffect(() => {
    if (!documentLoading && documentData) {
      if (documentData.content?.length > 0) {
        const entity = documentData.decode().content[0];
        setFile({
          entityId: entity.id,
          hash: entity.hash,
          label: entity.name,
          contentId: entity.contentId
        });
      } else if (externalCall) {
        setFile(null);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: `error.document.notFound` })
        });
      }
    }
  }, [addNotification, documentData, documentLoading, externalCall, msg]);

  const { data: relationships, loading: relationshipsLoading } = useQueryData(
    useGetEntityRelationshipsQuery,
    file?.entityId ?? -1,
    undefined,
    null
  );

  useEffect(() => {
    if (file && !relationshipsLoading && relationships != null) {
      setLinkedFiles(
        relationships.map((relationship) => ({
          relationType: relationship.type.code,
          relationSide: relationship.side,
          entityId: relationship.entity.id,
          label: relationship.entity.name,
          hash: relationship.entity.hash,
          contentId: relationship.entity.contentId
        }))
      );
    }
  }, [file, relationships, relationshipsLoading]);

  const shouldDisplayViewer = file != null || linkedFile != null;

  const handleRelatedDocumentClick = (newFile) => {
    setFile(newFile);
    setLinkedFile(null);
  };

  const onFileChanged = (event, entityId) => {
    if (entityId) {
      const file = linkedFiles.find((file) => file.entityId === entityId);
      setLinkedFile(file);
    }
  };

  return (
    <Fade in timeout={(2000, 500)}>
      <MDBox
        p={2}
        bgColor="container"
        display="flex"
        flex={1}
        sx={{
          flexDirection: { xs: 'column', xxl: 'row' }
        }}
      >
        {shouldDisplayViewer ? (
          <>
            <MDBox flex={1} display="flex">
              {file &&
                (!isCarouselEmpty || !xlWidthUp ? (
                  <>
                    <Tooltip title={msg({ id: 'action.show.properties' })} placement="right">
                      <Fab
                        color="primary"
                        aria-label="open properties"
                        onClick={() => setMetadataOpen(true)}
                        size="medium"
                        sx={{
                          alignSelf: 'center',
                          padding: '0 !important',
                          position: 'absolute',
                          left: '28px',
                          '&:hover': { backgroundColor: darken(primaryColor, 0.2) }
                        }}
                      >
                        <ArrowForwardIosRoundedIcon fontSize="large" />
                      </Fab>
                    </Tooltip>
                    <SwipeableDrawer
                      anchor="left"
                      open={metadataOpen}
                      onClose={() => setMetadataOpen(false)}
                      onOpen={() => setMetadataOpen(true)}
                      sx={{
                        zIndex: '2000',
                        '& .MuiDrawer-paper': {
                          boxSizing: 'border-box',
                          width: '45%',
                          minWidth: '400px',
                          height: '100%',
                          margin: 0,
                          p: 2
                        }
                      }}
                    >
                      <FileMetadatas
                        entityId={file.entityId}
                        fileViewerConf={fileViewerConf}
                        onEntityUpdated={onEntityUpdated}
                      />
                    </SwipeableDrawer>
                  </>
                ) : (
                  <MDBox
                    flex={0.35}
                    mr={2}
                    sx={{
                      minHeight: '600px'
                    }}
                  >
                    <FileMetadatas
                      entityId={file.entityId}
                      fileViewerConf={fileViewerConf}
                      onEntityUpdated={onEntityUpdated}
                    />
                  </MDBox>
                ))}

              <MDBox
                flex={1}
                sx={{
                  minHeight: '800px',
                  boxShadow: 'rgba(149, 157, 165, 0.2) 1px -1px 24px 9px'
                }}
              >
                {linkedFiles?.length > 1 && (
                  <ToggleButtonGroup
                    exclusive
                    value={linkedFile?.entityId || file.entityId}
                    onChange={onFileChanged}
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    {linkedFiles?.map((f) => (
                      <ToggleButton key={f.entityId} value={f.entityId} size="small">
                        <Tooltip
                          title={msg({
                            id: `preview.files.${f.relationType}_${f.relationSide}.label`
                          })}
                        >
                          <Icon fontSize="medium">
                            {msg({ id: `preview.files.${f.relationType}_${f.relationSide}.icon` })}
                          </Icon>
                        </Tooltip>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                <FileViewer document={linkedFile || file} />
              </MDBox>
            </MDBox>
            {StringUtils.toBoolean(fileViewerConf?.relatedDocument?.enabled) &&
              file &&
              !isCarouselEmpty && (
                <MDBox
                  display="flex"
                  width={{ sx: '100%', xxl: '30%' }}
                  shadow="md"
                  sx={{
                    flexDirection: 'column'
                  }}
                  mt={{ xs: 2, xxl: 0 }}
                  pl={1}
                >
                  <RelatedDocumentsCarousel
                    entityId={file.entityId}
                    onDocumentClick={handleRelatedDocumentClick}
                    conf={fileViewerConf?.relatedDocument}
                    direction={xlWidthUp ? 'column' : 'row'}
                    onCarouselEmpty={handleCarouselStatus}
                  />
                </MDBox>
              )}
          </>
        ) : (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        )}
      </MDBox>
    </Fade>
  );
}

FileViewerContainer.defaultProps = {
  onEntityUpdated: () => {}
};

FileViewerContainer.propTypes = {
  onEntityUpdated: PropTypes.func // Expected: (entity: object) => void
};

export default FileViewerContainer;
